import { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import ebiOfficeService from "api/ebiOfficeService";
import Button from "components/Button";
import ConfirmModal from "components/ConfirmModal";
import HollowButton from "components/HollowButton";
import Input from "components/Input";
import { EEbiAgentStatus, IEbiAgent, IEbiAgentCreated } from "dto/Ebi/IEbiOffice";
import { IError } from "dto/IError";
import { hasCompanyPrivateKeys } from "utils/companyContentKeys";
import { buildEbiAgentGrant } from "utils/ebiAgentGrant";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";
import { FC, IEbiAgentsSectionProps } from "./typings";

// Usługa przeszyfrowań na serwerze urzędu (ebi-agent, LegalMail/ebi-agent): lista zarejestrowanych usług, dodanie
// nowej (jednorazowy kod rejestracji pokazywany raz), wydanie klucza firmy (grant PGP(usługa.pub){firma.priv}
// budowany w TEJ przeglądarce — utils/ebiAgentGrant) i unieważnienie. Sekcję widzi administrator; klucz wydaje
// pracownik, który ma klucz firmy w pamięci przeglądarki.

const formatDate = (value?: string | null): string =>
  value ? moment(value).format("DD.MM.YYYY HH:mm") : "";

const EbiAgentsSection: FC<IEbiAgentsSectionProps> = ({ enabled }) => {
  const { t } = useTranslation();
  const { data, error, refetch } = ebiOfficeService.useGetAgents(enabled);
  const { mutate: create, isLoading: isCreating } = ebiOfficeService.useCreateAgent();
  const { mutate: grant } = ebiOfficeService.useGrantAgentKey();
  const { mutate: revoke, isLoading: isRevoking } = ebiOfficeService.useRevokeAgent();

  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [created, setCreated] = useState<IEbiAgentCreated | null>(null);
  const [grantingId, setGrantingId] = useState<string | null>(null);
  const [toRevoke, setToRevoke] = useState<IEbiAgent | null>(null);

  // Błąd z komunikatem BE (brak firmy, brak uprawnień) pokazujemy dosłownie; odpowiedź bez komunikatu (serwer bez tej
  // trasy odpowiada stroną HTML — patrz api/ebiOfficeService.getAgents) to informacja o nieobsługiwanej funkcji.
  const errorText = error ? getErrorMessage(error) || t("ebiOffice.agents.unsupported") : null;
  const isUnsupported = !!errorText;
  const agents = Array.isArray(data?.data)
    ? data.data.filter((a) => a.Status !== EEbiAgentStatus.Revoked)
    : [];
  const canGrant = hasCompanyPrivateKeys();

  const notifyError = (error: unknown) =>
    notification({
      type: ENotificationType.ERROR,
      title: t("ebiOffice.agents.title"),
      text:
        getErrorMessage(error as IError) ||
        (error instanceof Error ? error.message : "") ||
        t("errors.somethingWentWrongShort"),
    });

  const handleCreate = () =>
    create(name.trim(), {
      onSuccess: ({ data: result }) => {
        if (!result?.Agent?.Id || typeof result.EnrollToken !== "string") {
          notifyError(new Error(t("ebiOffice.agents.unsupported")));
          return;
        }

        setCreated(result);
        setName("");
        setShowAdd(false);
        refetch();
      },
      onError: notifyError,
    });

  const handleGrant = async (agent: IEbiAgent) => {
    if (!agent.PublicKey) return;
    setGrantingId(agent.Id);

    try {
      const payload = await buildEbiAgentGrant(agent.PublicKey);

      grant(
        { agentId: agent.Id, ...payload },
        {
          onSuccess: () => {
            refetch();
            notification({
              type: ENotificationType.SUCCESS,
              title: t("ebiOffice.agents.title"),
              text: t("ebiOffice.agents.granted", { name: agent.Name }),
            });
          },
          onError: notifyError,
          onSettled: () => setGrantingId(null),
        },
      );
    } catch (error) {
      setGrantingId(null);
      notifyError(error);
    }
  };

  const handleRevoke = () => {
    if (!toRevoke) return;
    const agent = toRevoke;
    setToRevoke(null);
    revoke(agent.Id, { onSuccess: () => refetch(), onError: notifyError });
  };

  const copyToken = () => {
    if (!created) return;

    navigator.clipboard
      .writeText(created.EnrollToken)
      .then(() =>
        notification({
          type: ENotificationType.INFO,
          title: t("ebiOffice.agents.title"),
          text: t("ebiOffice.agents.copied"),
        }),
      )
      .catch(() => undefined);
  };

  const statusOf = (agent: IEbiAgent): { text: string; isError: boolean } => {
    if (agent.Status === EEbiAgentStatus.Enrolling) {
      return {
        text: t("ebiOffice.agents.status.enrolling", { date: formatDate(agent.EnrollTokenExpires) }),
        isError: false,
      };
    }

    if (!agent.HasKey) return { text: t("ebiOffice.agents.status.waitingForKey"), isError: true };
    if (!agent.KeyIsCurrent) return { text: t("ebiOffice.agents.status.staleKey"), isError: true };
    if (!agent.CanProcess) return { text: t("ebiOffice.agents.status.grantorLost"), isError: true };

    return { text: t("ebiOffice.agents.status.active"), isError: false };
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{t("ebiOffice.agents.title")}</div>
      <div className={styles.hint}>{t("ebiOffice.agents.description")}</div>

      {errorText && <div className={styles.error}>{errorText}</div>}

      {!isUnsupported && agents.length === 0 && !created && (
        <div className={styles.hint}>{t("ebiOffice.agents.empty")}</div>
      )}

      {agents.length > 0 && (
        <div className={styles.agentList}>
          {agents.map((agent) => {
            const status = statusOf(agent);

            return (
              <div key={agent.Id} className={styles.agent}>
                <div className={styles.agentHeader}>
                  <div className={styles.agentName}>
                    {agent.Name}
                    {agent.Host ? ` · ${agent.Host}` : ""}
                  </div>
                  <div className={status.isError ? styles.error : styles.hint}>{status.text}</div>
                </div>

                <div className={styles.hint}>
                  {agent.LastSeenDate
                    ? t("ebiOffice.agents.lastSeen", { date: formatDate(agent.LastSeenDate) })
                    : t("ebiOffice.agents.neverSeen")}
                  {" · "}
                  {t("ebiOffice.agents.sharedCount", { count: agent.SharedCount })}
                  {agent.GrantedBy
                    ? ` · ${t("ebiOffice.agents.grantedBy", {
                        name: agent.GrantedBy,
                        date: formatDate(agent.GrantedDate),
                      })}`
                    : ""}
                </div>

                {agent.LastError && (
                  <div className={styles.error}>
                    {t("ebiOffice.agents.lastError", {
                      date: formatDate(agent.LastErrorDate),
                      error: agent.LastError,
                    })}
                  </div>
                )}

                <div className={styles.agentActions}>
                  {agent.Status === EEbiAgentStatus.Active && (
                    <HollowButton
                      text={agent.HasKey ? t("ebiOffice.agents.regrant") : t("ebiOffice.agents.grant")}
                      onClick={() => handleGrant(agent)}
                      disabled={!canGrant || grantingId === agent.Id}
                    />
                  )}
                  <HollowButton
                    text={t("ebiOffice.agents.revoke")}
                    onClick={() => setToRevoke(agent)}
                    disabled={isRevoking}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!canGrant && agents.some((a) => a.Status === EEbiAgentStatus.Active) && (
        <div className={styles.hint}>{t("ebiOffice.agents.noCompanyKey")}</div>
      )}

      {created && (
        <div className={styles.tokenBox}>
          <div className={styles.tokenTitle}>
            {t("ebiOffice.agents.tokenTitle", { name: created.Agent.Name })}
          </div>
          <div className={styles.hint}>
            {t("ebiOffice.agents.tokenHint", { date: formatDate(created.EnrollTokenExpires) })}
          </div>
          <div className={styles.token}>{created.EnrollToken}</div>
          <div className={styles.agentActions}>
            <HollowButton text={t("ebiOffice.agents.copyToken")} onClick={copyToken} />
            <HollowButton text={t("ebiOffice.agents.hideToken")} onClick={() => setCreated(null)} />
          </div>
        </div>
      )}

      {isUnsupported ? null : showAdd ? (
        <div className={styles.field}>
          <Input
            label={t("ebiOffice.agents.name")}
            value={name}
            onChange={(value) => setName(value.slice(0, 100))}
            placeholder={t("ebiOffice.agents.namePlaceholder")}
          />
          <div className={styles.actions}>
            <HollowButton
              text={t("common.cancel")}
              onClick={() => {
                setShowAdd(false);
                setName("");
              }}
            />
            <Button
              text={t("ebiOffice.agents.create")}
              onClick={handleCreate}
              disabled={!name.trim() || isCreating}
            />
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          <HollowButton text={t("ebiOffice.agents.add")} onClick={() => setShowAdd(true)} />
        </div>
      )}

      {toRevoke && (
        <ConfirmModal
          title={t("ebiOffice.agents.revokeTitle")}
          text={t("ebiOffice.agents.revokeText", { name: toRevoke.Name })}
          acceptLabel={t("ebiOffice.agents.revoke")}
          cancelLabel={t("common.cancel")}
          onAccept={handleRevoke}
          onCancel={() => setToRevoke(null)}
          showDontShowNextTime={false}
        />
      )}
    </div>
  );
};

export default EbiAgentsSection;
